import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import QuantitySelect from './QuantitySelect';
import ManifestItemsPicker from './ManifestItemsPicker';
import { normalizeItemTypes } from '../api';

import '../../stylesheets/components/ManifestInput.scss';

type SelectedVariant = {
  id: string;
  quantity: number;
};

type ItemVariant = {
  id: string;
  full_name: string;
  variant_name: string;
  billing_units: number;
  itemType: string;
  [key: string]: any;
};

type ItemType = {
  id: string;
  name: string;
  aliases: string;
  popularity: number;
  for_internal_client_use: boolean;
  icon: { svg: string };
  variants: string[];
  [key: string]: any;
};

type Entities = {
  itemTypes: Record<string, ItemType>;
  itemVariants: Record<string, ItemVariant>;
};

type ManifestListProps = {
  itemVariants: (ItemVariant & { quantity: number })[];
  onChangeQuantity: (variantId: string, quantity: number) => void;
};

const ManifestList: React.FC<ManifestListProps> = ({ itemVariants, onChangeQuantity }) => {
  if (itemVariants.length == 0) {
    return (
      <div className="alert alert-info alert-outline-coloured">
        <div className="alert-message">
          Choose some items from the list.
        </div>
      </div>
    );
  }

  return (
    <div className="list-group">
      {itemVariants.map(variant => (
        <div className="list-group-item d-flex justify-content-between align-items-center" key={variant.id}>
          <div className="flex-fill">{variant.full_name}</div>
          <QuantitySelect value={variant.quantity}
            onChange={(newQuantity: number) => onChangeQuantity(variant.id, newQuantity)} />
        </div>
      ))}
    </div>
  );
};

type ManifestInputProps = {
  name: string;
  value: SelectedVariant[];
  direction?: string;
  onChange?: (variants: SelectedVariant[]) => void;
  hideInternal?: boolean;
  client_id?: number;
};

const ManifestInput: React.FC<ManifestInputProps> = ({
  name,
  value: initialValue,
  direction,
  onChange,
  hideInternal,
  client_id,
}) => {
  const [entities, setEntities] = useState<Entities | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItemVariants, setSelectedItemVariants] = useState<SelectedVariant[]>(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedRef = useRef(selectedItemVariants);
  selectedRef.current = selectedItemVariants;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const entitiesRef = useRef(entities);
  entitiesRef.current = entities;

  const fetchItemTypes = useCallback(() => {
    const url = '/item_types';
    axios.get(API_BASE + url, {
      params: {
        direction,
        client_id,
      },
    })
      .then((response) => {
        setEntities(normalizeItemTypes(response.data.data).entities);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [direction, client_id]);

  useEffect(() => {
    if (window.STORE?.itemTypes) {
      setEntities(normalizeItemTypes(window.STORE.itemTypes).entities);
      setIsLoading(false);
    } else {
      fetchItemTypes();
    }
  }, [fetchItemTypes]);

  const getBillingUnits = useCallback((variants: SelectedVariant[]) => {
    if (!entitiesRef.current) return 0;
    return variants.reduce((sum, { id, quantity }) => {
      const variant = entitiesRef.current!.itemVariants[id];
      return sum + quantity * (variant?.billing_units ?? 0);
    }, 0);
  }, []);

  const getJSON = useCallback((variants: SelectedVariant[]) => {
    return JSON.stringify(variants);
  }, []);

  const fireChangeCallbacks = useCallback((newVariants: SelectedVariant[]) => {
    onChangeRef.current?.(newVariants);

    if (name.indexOf('delivery') != -1) {
      window.updateDeliveryCharge?.({
        units: getBillingUnits(newVariants),
      });
    }

    const manifestType = name.indexOf('delivery') != -1 ? 'delivery' : 'collection';
    window.updateBookingHints?.({
      [`${manifestType}_manifest_data`]: getJSON(newVariants),
    });
  }, [name, getBillingUnits, getJSON]);

  const handleAddToManifest = useCallback((variantId: string) => {
    setSelectedItemVariants(prev => {
      const existingSelection = prev.find(variant => variant.id == variantId);
      if (existingSelection) return prev;

      const newVariants = [...prev, { id: variantId, quantity: 1 }];
      setTimeout(() => fireChangeCallbacks(newVariants), 0);
      return newVariants;
    });
  }, [fireChangeCallbacks]);

  const handleChangeQuantity = useCallback((variantId: string, quantity: number) => {
    setSelectedItemVariants(prev => {
      const newVariants = prev.map(el => {
        if (el.id == variantId) {
          return { id: variantId, quantity };
        }
        return el;
      });
      setTimeout(() => fireChangeCallbacks(newVariants), 0);
      return newVariants;
    });
  }, [fireChangeCallbacks]);

  const getItemTypes = useMemo(() => {
    if (!entities) return [];
    return window.objectValues(entities.itemTypes).filter((itemType: ItemType) =>
      !hideInternal && !itemType.for_internal_client_use
    ).map((itemType: ItemType) => {
      return Object.assign({}, itemType, {
        variants: itemType.variants.map((variantId: string) => {
          return entities.itemVariants[variantId];
        }),
      });
    }).sort((a: ItemType, b: ItemType) => {
      return b.popularity - a.popularity;
    });
  }, [entities, hideInternal]);

  const getSelectedItemVariants = useMemo(() => {
    if (!entities) return [];
    return selectedItemVariants.map(({ id, quantity }) => {
      const variant = entities.itemVariants[id];
      return { ...variant, quantity };
    });
  }, [entities, selectedItemVariants]);

  const jsonValue = useMemo(() => getJSON(selectedItemVariants), [selectedItemVariants, getJSON]);

  if (isLoading) {
    return <p>Loading items...</p>;
  }

  return (
    <div className="row">
      <div className="col-sm-7">
        <ManifestList
          itemVariants={getSelectedItemVariants}
          onChangeQuantity={handleChangeQuantity} />
      </div>

      <div className="col-sm-5">
        <ManifestItemsPicker
          items={getItemTypes}
          onAdd={handleAddToManifest} />
      </div>

      <input type="hidden"
        name={name}
        defaultValue={jsonValue}
        ref={inputRef} />
    </div>
  );
};

export default ManifestInput;
